import { ApiErrorState } from "../shared/ApiErrorState";
import { ChampionStatsTable } from "./ChampionStatsTable";
import { RoutePageMeta } from "../../seo/RoutePageMeta";
import { useChampionStats } from "./hooks/useChampionStats";

export const ChampionStatsPage = () => {
  const {
    copy,
    seasons,
    selectedSeason,
    setSelectedSeason,
    rows,
    search,
    setSearch,
    roleFilter,
    setRoleFilter,
    championRoles,
    roleLabels,
    sortKey,
    sortDirection,
    handleSort,
    loading,
    refreshing,
    error,
    formatSeasonLabel,
    isLiveSeason,
    showDraftColumns,
    emptyMessage,
    roleFilterAvailable,
  } = useChampionStats();

  return (
    <div className="mx-auto box-border min-h-screen w-full max-w-[96rem] min-w-0 self-center bg-transparent px-[clamp(0.75rem,2vw,1.25rem)] pt-[12rem] pb-10 font-shentox text-[#e8e8ef] max-md:px-[0.85rem] max-md:pt-[11rem]">
      <RoutePageMeta page="CHAMPIONS" />
      <div className="flex w-full flex-col gap-5">
        <section className="grid grid-cols-[minmax(0,1fr)_auto] grid-rows-[auto_auto] items-start gap-x-6 gap-y-2 max-md:gap-x-4 max-[520px]:gap-y-3">
          <h1 className="col-start-1 row-start-1 m-0 text-[clamp(2rem,4vw,2.75rem)] font-bold tracking-[-0.02em] max-md:text-[clamp(1.65rem,7vw,2.25rem)]">
            {copy.TITLE}
          </h1>
          <div className="col-start-2 row-span-2 shrink-0 self-start text-right text-[0.9rem] leading-[1.4] text-[#8b8b9a] max-md:text-[0.82rem] max-[520px]:row-span-1 max-[520px]:row-start-1">
            {selectedSeason ? (
              <div className="flex flex-col items-end gap-[0.2rem]">
                <span className="text-[0.72rem] font-semibold tracking-[0.04em] uppercase">
                  {copy.SEASON}
                </span>
                <span className="font-semibold whitespace-nowrap text-[#e8e8ef] max-md:whitespace-normal">
                  {formatSeasonLabel(selectedSeason)}
                </span>
                {isLiveSeason ? (
                  <span className="block max-w-[15rem] text-right text-[0.78rem] leading-[1.35] text-[#5eead4] max-md:max-w-[10rem] max-md:text-[0.72rem] max-[520px]:max-w-[9.5rem]">
                    {copy.CURRENT_SEASON_NOTE}
                  </span>
                ) : null}
              </div>
            ) : (
              <span className="font-semibold whitespace-nowrap text-[#e8e8ef] max-md:whitespace-normal">
                {copy.NO_SEASON_DATA}
              </span>
            )}
          </div>
          <p className="col-start-1 row-start-2 m-0 max-w-[42rem] leading-[1.5] text-[#8b8b9a] max-md:max-w-none max-md:text-[0.92rem] max-md:leading-[1.45] max-[520px]:col-span-full">
            {copy.DESCRIPTION}
          </p>
        </section>

        <section className="surface-card w-full overflow-visible">
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-b-[var(--surface-card-border)] px-5 py-4 max-md:flex-col max-md:items-start">
            <h2 className="m-0 text-[1.1rem]">{copy.CHAMPIONS}</h2>
            <div className="flex flex-wrap gap-3 max-md:w-full">
              <label className="season-stats-filter">
                <span>{copy.FILTER_SEASON}</span>
                <select
                  className="season-stats-filter-select"
                  value={selectedSeason}
                  onChange={(event) => {
                    setSelectedSeason(event.target.value);
                  }}
                  disabled={!seasons.length}
                >
                  {seasons.map((season) => (
                    <option key={season} value={season}>
                      {formatSeasonLabel(season)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="season-stats-filter">
                <span>{copy.FILTER_ROLE}</span>
                <select
                  className="season-stats-filter-select disabled:cursor-not-allowed disabled:opacity-60"
                  value={roleFilter}
                  onChange={(event) => {
                    setRoleFilter(event.target.value);
                  }}
                  disabled={!roleFilterAvailable}
                  title={roleFilterAvailable ? undefined : copy.ROLE_UNAVAILABLE}
                  aria-description={roleFilterAvailable ? undefined : copy.ROLE_UNAVAILABLE}
                >
                  <option value="all">{copy.ALL_ROLES}</option>
                  {championRoles.map((role) => (
                    <option key={role} value={role}>
                      {roleLabels[role]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="season-stats-filter max-md:w-full">
                <span>{copy.FILTER_SEARCH}</span>
                <input
                  type="search"
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                  }}
                  placeholder={copy.SEARCH_PLACEHOLDER}
                  className="min-w-[10rem] rounded-md border border-[var(--surface-card-border)] bg-[#111118] px-[0.7rem] py-[0.4rem] text-[0.84rem] font-semibold text-[#e8e8ef] placeholder:font-normal placeholder:text-[#6b6b7d] focus:outline focus:outline-2 focus:outline-offset-1 focus:outline-[rgba(96,165,250,0.35)] max-md:w-full"
                />
              </label>
            </div>
          </div>

          <div>
            {loading ? (
              <div className="season-stats-empty">{copy.LOADING}</div>
            ) : error && !rows.length ? (
              <ApiErrorState message={error} className="season-stats-empty" />
            ) : (
              <>
                {error ? (
                  <ApiErrorState
                    message={error}
                    className="mx-5 mt-4 rounded-small border border-red-400/35 bg-red-400/[0.08] px-4 py-3 text-center text-[#fca5a5]"
                  />
                ) : null}
                <div
                  aria-busy={refreshing}
                  className={`transition-opacity duration-150 ${refreshing ? "opacity-60" : "opacity-100"}`}
                >
                  <ChampionStatsTable
                    rows={rows}
                    sortKey={sortKey}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                    showDraftColumns={showDraftColumns}
                    emptyMessage={emptyMessage}
                  />
                </div>
                <p className="sr-only" role="status">
                  {copy.RESULT_COUNT.replace("{count}", String(rows.length))}
                </p>
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};
